import { useEffect, useState } from "react";
import { Activity, AlertTriangle, TrendingUp, BarChart3, Car, CarFront, Truck, Wallet, Home, Wrench, CalendarCheck, Fuel, } from "lucide-react";
import Skeleton from '@mui/material/Skeleton';
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { TableCard } from "../../../../component/table/table-layout";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card, CardContent, CardHeader, CardTitle, KPI, TitleHeading } from "../../../../component/ui/layout";

const FleetVanOverview = () => {
    const apiCall = useApiCall();
    const [KPIData, setKPIData] = useState({});
    const [KPILoading, setKPILoading] = useState(false);

    const getKPIData = async () => {
        setKPILoading(false);
        try {
            const resp = await apiCall({ method: "get", url: `fleet-overview/my-organization-summary` });
            setKPIData(resp?.kpi_cards);
            const trend = resp.monthly_fleet_cost_trend;
            setTrendData({
                month: trend.map(i => i.month),
                car_rent: trend.map(i => i.car_rent),
                van_rent: trend.map(i => i.van_rent),
                cash_allowance: trend.map(i => i.cash_allowance),
                fuel_allowance: trend.map(i => i.fuel_allowance),
                maintenance_cost: trend.map(i => i.maintenance_cost),
            });
            setEmployeeData(resp?.fleet_composition_employee_chart);
            setVehicleData(resp?.fleet_composition_vehicle_chart);
        } catch (error) {
            console.warn("Error : ", error);
        } finally {
            setKPILoading(true);
        }
    }

    useEffect(() => {
        getKPIData();
    }, []);

    // LINE CHART
    const [trendData, setTrendData] = useState({});
    const lineOptions = {
        chart: { type: "line", backgroundColor: "transparent", height: 300 },
        title: { text: "" },
        xAxis: {
            categories: trendData?.month,
            lineColor: "#E5E7EB",
            labels: { style: { color: "#6B7280" } },
        },
        yAxis: {
            title: { text: "Count" },
            gridLineColor: "#F3F4F6",
            labels: { style: { color: "#6B7280" } },
        },
        legend: { align: "right", verticalAlign: "top" },
        series: [
            { name: "Car Rent", data: trendData?.car_rent || [], color: "#2563EB" },
            { name: "Van Rent", data: trendData?.van_rent || [], color: "#10B981" },
            { name: "Cash Allowance", data: trendData?.cash_allowance || [], color: "#F59E0B" },
            { name: "Fuel Allowance", data: trendData?.fuel_allowance || [], color: "#EF4444" },
            { name: "Maintenance Cost", data: trendData?.maintenance_cost || [], color: "#8B5CF6" },
        ],
        credits: { enabled: false },
    };

    // PIE CHART
    const [employeeData, setEmployeeData] = useState({});
    const [vehicleData, setVehicleData] = useState({});
    const [activePie, setActivePie] = useState("employee");
    const getPieSeries = () => {
        const source =
            activePie === "employee"
                ? employeeData
                : vehicleData;

        return Object.entries(source || {}).map(([key, val]) => ({
            count: val,
            name: key.replaceAll("_", " ").replaceAll("employees", ""),
            y: parseFloat(val),
        }));
    };
    const pieOptions = {
        chart: { type: "pie" },
        title: "",
        credits: { enabled: false },
        tooltip: {
            pointFormat:
                "{series.name}: <b>{point.count}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>",
        },
        plotOptions: {
            pie: {
                dataLabels: {
                    enabled: true,
                    format: "<b>{point.name}</b>: {point.percentage:.1f}% ({point.count})",
                },
            },
        },
        series: [
            {
                name: activePie === "employee" ? "Employees" : "Vehicles",
                colorByPoint: true,
                data: getPieSeries(),
            },
        ],
        legend: { enabled: false },
    };

    return (
        <>
            <div className="pt-[20px] overflow-hidden">
                <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
                    {/* Header */}
                    <div>
                        <TitleHeading>Fleets Overview</TitleHeading>
                    </div>

                    <TableCard className={"!p-[15px] !pr-[7px] h-full overflow-hidden"}>
                        {/* KPI Row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {KPILoading ? (
                                <>
                                    <KPI
                                        icon={Car}
                                        label="Total Vehicles"
                                        value={KPIData?.total_vehicles}
                                        sublabel={"Complete Fleet Count"}

                                    />
                                    <KPI
                                        icon={CarFront}
                                        label="Total Cars"
                                        value={KPIData?.total_cars}
                                        sublabel={`All Registered Cars`}

                                    />
                                    <KPI
                                        icon={Truck}
                                        label="Total Vans"
                                        value={KPIData?.total_vans}
                                        sublabel={`All Registered Vans`}
                                    />
                                    <KPI
                                        icon={Wallet}
                                        label="Monthly Cash Allowance"
                                        value={KPIData?.total_monthly_cash_allowance_amount}
                                        sublabel={`Total Payout Amount`}

                                    />
                                    <KPI
                                        icon={Home}
                                        label="Monthly Vehicle Rent"
                                        value={KPIData?.total_monthly_rent}
                                        sublabel={`Total Rent Cost`}

                                    />
                                    <KPI
                                        icon={Wrench}
                                        label="Maintenance (Current Month)"
                                        value={KPIData?.total_maintenance_current_month}
                                        sublabel={`Repairs This Month`}

                                    />
                                    <KPI
                                        icon={CalendarCheck}
                                        label="Maintenance (YTD)"
                                        value={KPIData?.total_maintenance_ytd}
                                        sublabel={`This Year Till Today Costs`}

                                    />
                                    <KPI
                                        icon={Fuel}
                                        label="Fuel Mileage Cost"
                                        value={KPIData?.fuel_mileage_cost_current_month}
                                        sublabel={`Current Month Fuel Cost`}

                                    />
                                    <KPI
                                        icon={Activity}
                                        label="Vehicles Under Repair"
                                        value={KPIData?.under_repair_vehicles}
                                        sublabel={`Currently Being Repaired`}

                                    />
                                    <KPI
                                        icon={AlertTriangle}
                                        label="Overdue Maintenance"
                                        value={KPIData?.maintenance_overdue}
                                        sublabel={`Service Past Due`}

                                    />
                                </>
                            ) : (
                                Array.from({ length: 10 }).map((_, index) => (
                                    <div key={index} className="bg-white shadow rounded-xl p-4 animate-pulse">
                                        {/* Top row: label + icon */}
                                        <div className="flex items-center justify-between mb-2">
                                            <Skeleton variant="text" width={80} height={16} />
                                            <Skeleton variant="circular" width={20} height={20} />
                                        </div>
                                        {/* Value row */}
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <Skeleton variant="text" width={60} height={32} />
                                            <Skeleton variant="rounded" width={40} height={20} />
                                        </div>
                                        {/* Sublabel */}
                                        <Skeleton variant="text" width={80} height={12} />
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
                            <Card className="col-span-3 min-h-[500px]">
                                <CardHeader className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5" />
                                        Monthly Fleet Cost Trend
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {
                                        KPILoading ? (
                                            <HighchartsReact highcharts={Highcharts} options={lineOptions} />
                                        ) : (
                                            <InnerLoader />
                                        )
                                    }
                                </CardContent>
                            </Card>

                            <Card className="col-span-2 min-h-[500px]">
                                <CardHeader className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2 capitalize">
                                        <BarChart3 className="h-5 w-5" /> Fleet Composition By {activePie}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {/* Toggle Legends */}
                                    <div className={`flex items-center justify-end gap-[15px]`}>
                                        {["employee", "vehicle"].map(label => (
                                            <div
                                                key={label}
                                                className={`flex items-center gap-[3px] cursor-pointer`}
                                                onClick={() => setActivePie(label)}
                                            >
                                                <div
                                                    className={`relative h-[1.5px] w-[16px] cursor-pointer ${activePie === label ? "bg-[#1FC16B]" : "bg-[#787878]"}`}
                                                >
                                                    <div className={`absolute top-1/2 left-[4px] h-[8px] w-[8px] rounded-full -translate-y-1/2 ${activePie === label ? "bg-[#1FC16B]" : "bg-[#787878]"}`} />
                                                </div>
                                                <p className="text-sm font-medium text-[#333] capitalize">{label}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {
                                        KPILoading ? (
                                            <HighchartsReact highcharts={Highcharts} options={pieOptions} />
                                        ) : (
                                            <InnerLoader />
                                        )
                                    }
                                </CardContent>
                            </Card>
                        </div>
                    </TableCard>
                </div>
            </div>
        </>
    );
}

export default FleetVanOverview